import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { LocalOfferRounded as LocalOfferIcon } from '@material-ui/icons';

import OrderItemTypeChip from '../Chips/OrderItemTypeChip.component';
import ProductSizeChip from '../../../products/components/Chips/ProductSizeChip.component';
import { formatPrice } from '../../../shared/utils/format-price.util';
import { Order } from '../../types/order.type';
import { OrderItem } from '../../types/order-item.type';

interface OrderItemsTableHeader {
  key: string;
  label: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
}

const HEADERS: OrderItemsTableHeader[] = [
  { key: 'type', label: 'Type', minWidth: 50, align: 'left' },
  { key: 'name', label: 'Article', minWidth: 220, align: 'left' },
  { key: 'category', label: 'Catégorie', minWidth: 120, align: 'left' },
  { key: 'size', label: 'Taille', minWidth: 60, align: 'center' },
  { key: 'labels', label: 'Étiquettes', minWidth: 80, align: 'center' },
  { key: 'tvaRate', label: 'TVA', minWidth: 60, align: 'center' },
  { key: 'quantity', label: 'Qté', minWidth: 60, align: 'right' },
  { key: 'total', label: 'Total TTC', minWidth: 100, align: 'right' },
];

const useStyles = makeStyles(() => ({
  root: {},
  paperRoot: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerCell: {
    fontWeight: 'bold',
  },
  labelIconOn: {
    width: 18,
    height: 18,
    fill: '#4caf50',
  },
  labelIconOff: {
    width: 18,
    height: 18,
    fill: '#9e9e9e',
  },
  secondaryLine: {
    display: 'block',
    fontSize: 12,
    color: '#757575',
  },
  amountCell: {
    fontVariantNumeric: 'tabular-nums',
  },
  totals: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1.5rem',
    padding: '1rem',

    '& > p': {
      margin: 0,
    },
  },
}));

interface Props {
  order: Order;
}

export default function OrderItemsTable({ order }: Props) {
  const classes = useStyles();

  const { items, totals } = order;

  return (
    <section className={classes.root}>
      <h4>Produits</h4>
      <Paper className={classes.paperRoot}>
        <TableContainer>
          <Table size="small" aria-label="Articles de la commande">
            <TableHead>
              <TableRow>
                {HEADERS.map((header) => (
                  <TableCell
                    key={header.key}
                    align={header.align}
                    className={classes.headerCell}
                    style={{ minWidth: header.minWidth }}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: OrderItem, itemIndex: number) => (
                <TableRow hover key={`${item.ref}-${itemIndex}`}>
                  <TableCell align="left">
                    <OrderItemTypeChip type={item.type} size="small" />
                  </TableCell>
                  <TableCell align="left">
                    {item.name}
                    <span className={classes.secondaryLine}>Réf. {item.ref}</span>
                  </TableCell>
                  <TableCell align="left">
                    {item.category}
                    {item.subcategory && <span className={classes.secondaryLine}>{item.subcategory}</span>}
                  </TableCell>
                  <TableCell align="center">{item.size && <ProductSizeChip size={item.size} />}</TableCell>
                  <TableCell align="center">
                    <LocalOfferIcon
                      className={item.noLabel ? classes.labelIconOff : classes.labelIconOn}
                      titleAccess={item.noLabel ? 'Sans étiquette' : 'Avec étiquette'}
                    />
                  </TableCell>
                  <TableCell align="center">{item.tvaRate}%</TableCell>
                  <TableCell align="right" className={classes.amountCell}>
                    {item.quantity}
                  </TableCell>
                  <TableCell align="right" className={classes.amountCell}>
                    {formatPrice(item.price * item.quantity)}
                    <span className={classes.secondaryLine}>{formatPrice(item.price)}/u</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div className={classes.totals}>
          {totals.totalDiscount > 0 && <p>Remise : {totals.totalDiscount.toFixed(2)} €</p>}
          <p>Total HT : {totals.totalHt.toFixed(2)} €</p>
          <p>
            <strong>Total TTC : {totals.totalTtc.toFixed(2)} €</strong>
          </p>
        </div>
      </Paper>
    </section>
  );
}
